import { ArrowUpRight, Phone, Mail, MapPin, Instagram, Music2, MessageSquare } from "lucide-react";
import { WhatsAppIcon } from "@/components/oneyork/icons";
import { BRAND, waLink, smsLink } from "@/lib/oneyork";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-36 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center sm:text-left">
          <span className="text-xs uppercase tracking-[0.28em] text-[#d4af37] font-medium">
            Contact
          </span>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            <span className="sr-only">Contact ONE YORK Auto Detailing in York Region — </span>
            Let&apos;s get in{" "}
            <span className="italic font-medium text-white/70">touch.</span>
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-xl">
            Reach out directly on WhatsApp or through any of the channels below
            — we typically respond within the same day.
          </p>
        </div>

        <div className="mt-12 lg:mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
          <a
            data-testid="contact-phone"
            href={`tel:${BRAND.phoneTel}`}
            className="flex items-start gap-4 group"
          >
            <Phone
              className="text-[#d4af37] mt-1"
              size={20}
              strokeWidth={1.5}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/40">
                Phone
              </div>
              <div className="mt-1 text-xl text-white group-hover:text-[#d4af37] transition-colors">
                {BRAND.phone}
              </div>
            </div>
          </a>

          <a
            data-testid="contact-email"
            href={`mailto:${BRAND.email}`}
            className="flex items-start gap-4 group"
          >
            <Mail className="text-[#d4af37] mt-1" size={20} strokeWidth={1.5} />
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/40">
                Email
              </div>
              <div className="mt-1 text-xl text-white group-hover:text-[#d4af37] transition-colors break-all">
                {BRAND.email}
              </div>
            </div>
          </a>

          <a
            data-testid="contact-instagram"
            href={`https://instagram.com/${BRAND.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-4 group"
          >
            <Instagram
              className="text-[#d4af37] mt-1"
              size={20}
              strokeWidth={1.5}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/40">
                Instagram
              </div>
              <div className="mt-1 text-xl text-white group-hover:text-[#d4af37] transition-colors">
                @{BRAND.instagram}
              </div>
            </div>
          </a>

          <a
            data-testid="contact-tiktok"
            href={`https://tiktok.com/@${BRAND.tiktok}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-4 group"
          >
            <Music2
              className="text-[#d4af37] mt-1"
              size={20}
              strokeWidth={1.5}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/40">
                TikTok
              </div>
              <div className="mt-1 text-xl text-white group-hover:text-[#d4af37] transition-colors">
                @{BRAND.tiktok}
              </div>
            </div>
          </a>

          <div className="flex items-start gap-4 sm:col-span-2">
            <MapPin
              className="text-[#d4af37] mt-1"
              size={20}
              strokeWidth={1.5}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/40">
                Service Area
              </div>
              <div className="mt-1 text-xl text-white">{BRAND.region}</div>
              <div className="text-xs text-white/45 mt-2 leading-relaxed">
                {BRAND.areas.join(" · ")}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row gap-4">
          <a
            data-testid="contact-whatsapp-cta"
            href={waLink("Hi One York,")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold px-8 py-4 rounded-full transition-colors"
          >
            <WhatsAppIcon width={17} height={17} />
            WhatsApp Chat
          </a>
          <a
            data-testid="contact-sms-cta"
            href={smsLink("Hi One York,")}
            className="inline-flex items-center justify-center gap-2 text-white font-semibold px-8 py-4 rounded-full transition-colors"
            style={{
              border: "1px solid rgba(255,255,255,0.28)",
              background:
                "linear-gradient(140deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.45), 0 6px 18px -6px rgba(0,0,0,0.4)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
            }}
          >
            <MessageSquare size={16} strokeWidth={1.8} />
            Text Message
          </a>
          <a
            data-testid="contact-call-cta"
            href={`tel:${BRAND.phoneTel}`}
            className="inline-flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-[#f2c94c] text-black font-semibold px-8 py-4 rounded-full transition-colors"
          >
            Call {BRAND.phone}
          </a>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] uppercase tracking-[0.22em] text-white/40">
          <div>© {new Date().getFullYear()} ONE YORK AUTO DETAILING</div>
          <div>FEYNLAB Certified · CARFAX Registered</div>
        </div>
      </div>
    </section>
  );
}
